import React, { useEffect, useState } from 'react';
import agent from '../../../services/Api/agent';
import LoadingComponent from '../../../components/LoadingComponent';
import { LocationDetailDto } from '../../../models/LocationDetail';
import { defaultThresholds, getMonitoringStatus } from '../config/thresholds';

interface LocationDetailDialogProps {
  locationId: number;
  locationName: string;
  isOpen: boolean;
  onClose: () => void;
  monitoringType?: 'noise' | 'tsp' | 'vibration';
  currentValue?: number;
  timestamp?: string; // 添加時間戳以檢查數據是否過時
  buttonRef?: React.RefObject<HTMLButtonElement>; // 按鈕引用，用於定位 tooltip
  alertThreshold?: number | null; // Alert threshold from LocationGroup
  unit?: string; // Unit for the monitoring value
}

const LocationDetailDialog: React.FC<LocationDetailDialogProps> = ({
  locationId,
  locationName,
  isOpen,
  onClose,
  monitoringType,
  currentValue,
  timestamp,
  buttonRef,
  alertThreshold,
  unit,
}) => {
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState<LocationDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<{ top: number; right?: number; left?: number }>({ top: 0 });
  const [showOnRight, setShowOnRight] = useState(false); // 是否顯示在右側

  useEffect(() => {
    if (isOpen && locationId) {
      fetchLocationDetails();
    }
  }, [isOpen, locationId]);

  // 計算 tooltip 位置
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 320; // max-w-[320px]
      const spaceOnLeft = buttonRect.left;
      const spaceOnRight = window.innerWidth - buttonRect.right;

      // 如果左側空間不足（小於 tooltip 寬度），則顯示在右側
      const shouldShowOnRight = spaceOnLeft < tooltipWidth;
      setShowOnRight(shouldShowOnRight);

      if (shouldShowOnRight) {
        // 顯示在右側（按鈕右側）：tooltip 左邊緣對齊按鈕右邊緣
        setPosition({
          top: buttonRect.bottom + 8, // 按鈕下方 8px
          left: buttonRect.right - 35, // tooltip 左邊緣在按鈕右邊緣 + 8px
          right: undefined,
        });
      } else {
        // 顯示在左側（按鈕左側）：tooltip 右邊緣對齊按鈕左邊緣
        setPosition({
          top: buttonRect.bottom + 8, // 按鈕下方 8px
          left: undefined,
          right: window.innerWidth - buttonRect.left - 50, // tooltip 右邊緣在按鈕左邊緣 + 8px
        });
      }
    }
  }, [isOpen, buttonRef]);

  const fetchLocationDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agent.Locations.getLocationDetail(locationId);
      setDetailData(data);
    } catch (err) {
      console.error('Failed to fetch location details:', err);
      setError('Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  // 檢查數據是否過時（超過 1 分鐘）- 與 MonitoringCard 邏輯一致
  const isDataStale = (): boolean => {
    if (!timestamp) return true; // 沒有時間戳視為過時
    try {
      const dataTime = new Date(timestamp);
      const now = new Date();
      const diffInMinutes = (now.getTime() - dataTime.getTime()) / (1000 * 60);
      return diffInMinutes > 1; // 超過 1 分鐘視為過時
    } catch {
      return true; // 解析失敗視為過時
    }
  };

  // 根據監控類型和當前值計算警報級別 - 與 MonitoringCard 邏輯一致
  const getAlarmLevelFromThreshold = (): string => {
    if (!monitoringType || currentValue === undefined) {
      return detailData?.alarmLevel || 'None';
    }

    //console.log("alertThreshold", alertThreshold);
    // 如果提供了 alertThreshold，使用動態閾值
    if (alertThreshold !== null && alertThreshold !== undefined) {
      // 使用傳入的 unit，如果沒有則使用默認閾值的單位
      const defaultConfig = defaultThresholds[monitoringType];
      const displayUnit = unit || defaultConfig.unit || '';
      return `${alertThreshold} ${displayUnit}`;
    }

    // 否則使用默認閾值
    const config = defaultThresholds[monitoringType];
    return `${config.max} ${config.unit}`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 背景遮罩 - 點擊關閉 */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      {/* Tooltip 內容 */}
      <div
        className="fixed z-50 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 min-w-[280px] max-w-[320px]"
        style={{
          top: `${position.top}px`,
          ...(showOnRight
            ? { left: `${position.left}px`, right: 'auto' }
            : { right: `${position.right}px`, left: 'auto' }
          ),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 箭頭 - 根據顯示位置調整，指向按鈕 */}
        <div
          className={`absolute -top-2 w-4 h-4 bg-gray-800 border-l border-t border-gray-700 transform rotate-45 ${
            showOnRight ? 'left-4' : 'right-4'
          }`}
        ></div>

        {/* 關閉按鈕 */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors z-10"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* 內容 */}
        <div className="p-4">
          <h3 className="text-sm font-semibold text-white mb-3 pr-6">{locationName}</h3>

          {loading ? (
            <div className="py-4">
              <LoadingComponent content="Loading..." />
            </div>
          ) : error ? (
            <div className="text-red-400 text-xs text-center py-2">{error}</div>
          ) : detailData ? (
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                <span className="text-gray-400 font-medium">MAC:</span>
                <span className="text-white">{detailData.mac || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                <span className="text-gray-400 font-medium">Alarm Level:</span>
                <span className="text-white">{getAlarmLevelFromThreshold()}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                <span className="text-gray-400 font-medium">Last Alarm:</span>
                <span className="text-white text-xs">{formatDate(detailData.lastAlarm)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="text-gray-400 font-medium">Status:</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    isDataStale()
                      ? 'bg-gray-500 text-white' // 數據過時使用灰色
                      : monitoringType && currentValue !== undefined
                      ? (() => {
                          const status = getMonitoringStatus(currentValue, monitoringType, undefined, alertThreshold);
                          if (status === 'alert') return 'bg-red-700 text-white';
                          return 'bg-blue-600 text-white';
                        })()
                      : detailData.status === 'Active'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-500 text-white'
                  }`}
                >
                  {isDataStale()
                    ? 'Inactive'
                    : monitoringType && currentValue !== undefined
                    ? (() => {
                        const status = getMonitoringStatus(currentValue, monitoringType, undefined, alertThreshold);
                        if (status === 'alert') return 'Alert';
                        return 'Normal';
                      })()
                    : detailData.status === 'Active'
                    ? 'Active'
                    : 'Inactive'}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default LocationDetailDialog;

